<template>

    <div v-show="isShowingPreviewDesign" class="process-label-design-sidebar p-3 pt-4 mt-4 z-10"
        :style="`width: ${width}`">

        <br />

        <b-button variant="light" @click="handleClosePreview" class="d-flex align-items-center">
            <i class="fa fa-chevron-left p-0 mr-2"></i>
            CERRAR VISTA
        </b-button>

        <b-card no-body class="mt-3 border-0">

            <!-- Seleccionar producto -->
            <b-col md="12" class="mt-2">
                <b-form-group>
                    <label for="product" class="text-muted font-weight-bold">Seleccionar producto (*)</label>
                    <el-select ref="refProductSelect" id="product" name="product" v-model="paramProduct" filterable remote clearable
                no-data-text="No hay datos que coincidan" no-match-text="No hay datos que coincidan"
                        :remote-method="onHandleGetProducts" @change="onHandleChangeFilterProduct">
                        <el-option v-for="product in productsPreviewLabelDesigner" :key="product.id"
                            :label="product.product_name" :value="product.id" />
                    </el-select>
                </b-form-group>
            </b-col>

            <!-- Área de visualización del PDF -->
            <div id="pdf-preview-area" ref="pdfPreviewArea" class="px-2 bg-white w-100">

                <!-- Estado: Cargando -->
                <div v-if="previewLoading" class="text-center py-5">
                    <b-spinner variant="primary" label="Generando vista previa..."></b-spinner>
                    <p class="mt-3 text-muted">Generando vista previa con el microservicio...</p>
                    <small class="text-muted">Este proceso puede tardar unos segundos</small>
                </div>

                <!-- Estado: Error -->
                <b-alert v-else-if="previewError" variant="danger" show class="mb-3">
                    <i class="fa fa-exclamation-triangle mr-2"></i>
                    <strong>Error al generar vista previa:</strong>
                    <br />
                    {{ previewError }}
                    <br />
                    <b-button 
                        variant="outline-danger" 
                        size="sm" 
                        class="mt-2"
                        @click="retryGeneratePreview"
                    >
                        <i class="fa fa-refresh mr-1"></i>
                        Reintentar
                    </b-button>
                </b-alert>

                <!-- Estado: PDF cargado -->
                <div v-else-if="previewPdfUrl" class="preview-pdf-container">
                    <PdfViewer 
                        :src="previewPdfUrl" 
                        :fileName="'preview.pdf'"
                        :initialScale="1.0"
                        :hideToolbar="true"
                        :autoFit="true"
                        :lightTheme="true"
                    />
                </div>

                <!-- Estado: Sin producto seleccionado -->
                <div v-else class="text-center py-5 text-muted empty-state">
                    <i class="fa fa-file-pdf-o empty-state-icon"></i>
                    <p class="mt-3 mb-1">Selecciona un producto para generar la vista previa</p>
                </div>

                <br />

                <!-- A tener en cuenta -->
                <b-alert variant="warning" show class="mb-0 mt-3">
                    <i class="fa fa-info-circle mr-2"></i>
                    <small>
                        <strong>NOTA</strong>
                        <br />
                        <li>El contenido se adaptará automáticamente al espacio disponible en la etiqueta.</li>
                        <li>Esta es una vista previa rápida de la etiqueta; la impresión no está habilitada en este modo.</li>
                        <li>Para imprimir la etiqueta, primero guarda el diseño y luego realiza la impresión desde el módulo de productos.</li>
                    </small>
                </b-alert>

                <!-- NO ta importante sobre impresión -->
                <b-alert variant="danger" show class="mb-0 mt-1">
                    <i class="fa fa-info-circle mr-2"></i>
                    <small>
                        <strong>IMPORTANTE</strong>
                        <br />
                        <li>Todo el contenido dentro del diseñador será parte de la etiqueta e impresión.</li>
                    </small>
                </b-alert>

            </div>
            <br />

        </b-card>

    </div>
</template>

<script>

// Importaciones generales
import { mapMutations, mapState, mapActions } from 'vuex';

// Componente para visualizar PDF
import PdfViewer from '@/components/PdfViewer.vue';

export default {
    name: 'labelDesignerPreviewDesign',

    components: {
        PdfViewer
    },

    props: {
        width: {
            type: String,
            default: '700pt',
            required: false
        }
    },

    data() {
        return {
            paramProduct: null
        }
    },

    computed: {

        ...mapState('labelDesigner', [
            'isShowingPreviewDesign',
            'productsPreviewLabelDesigner',
            'productPreviewLabelDesigner',
            'selectedLayout',
            'previewLoading',
            'previewError',
            'previewPdfUrl'
        ]),

        // Filtro de configuración desde el módulo de productos/labelDesigner
        ...mapState('labelDesignerToProducts', [
            'filter'
        ]),

    },

    watch: {

        /**
         * Cuando cambia el producto seleccionado, generar nueva vista previa.
         */
        productPreviewLabelDesigner(newProduct) {
            if (newProduct) {
                this.generatePreview();
            }
        },

        /*
         * Cuando cambia el diseño seleccionado
         * limpiar la vista previa anterior para evitar mostrar contenido obsoleto.
         */
        selectedLayout: {
            handler(newLayout, oldLayout) {
                const newId = newLayout && newLayout.id;
                const oldId = oldLayout && oldLayout.id;
                
                if (newId !== oldId) {
                    this.clearPreview();
                    this.paramProduct = null;
                    this.setProductPreviewLabelDesigner(null);
                }
            },
            deep: true
        },

        /**
         * Limpiar preview cuando se cierra el panel.
         */
        isShowingPreviewDesign(isShowing) {
            if (!isShowing) {
                this.clearPreview();
                this.paramProduct = null;
            } else {
                this.$nextTick(() => {
                    setTimeout(() => {
                        if (this.$refs.refProductSelect) {
                            this.$refs.refProductSelect.focus();
                        }
                    }, 100);
                });
            }
        }

    },

    methods: {

        ...mapMutations('labelDesigner', [
            'setIsShowingPreviewDesign',
            'setProductPreviewLabelDesigner',
            'clearPreview'
        ]),

        ...mapActions('labelDesigner', [
            'getProductsPreviewLabelDesigner',
            'previewDesignToProducts'
        ]),

        /**
         * Cerrar el panel de vista previa y limpiar recursos.
         */
        handleClosePreview() {
            this.clearPreview();
            this.paramProduct = null;
            this.setProductPreviewLabelDesigner(null);
            this.setIsShowingPreviewDesign(false);
        },

        /**
         * Buscar productos para previsualización.
         * 
         * @param {string} parameter - Texto de búsqueda
         */
        async onHandleGetProducts(parameter) {
            if (!(parameter && typeof parameter === 'string' && parameter.length >= 3)) return;

            let payload = { parameter };
            if (this.selectedLayout.company_id) {
                payload.company_id = this.selectedLayout.company_id;
            }
            await this.getProductsPreviewLabelDesigner(payload);
        },

        /**
         * Manejar cambio de producto seleccionado.
         * 
         * @param {number} productId - ID del producto seleccionado
         */
        onHandleChangeFilterProduct(productId) {
            const productData = this.productsPreviewLabelDesigner.find(x => x.id === productId);

            if (!productData) {
                this.paramProduct = null;
                this.setProductPreviewLabelDesigner(null);
                this.clearPreview();
                return;
            }

            this.setProductPreviewLabelDesigner(productData);
        },

        /**
         * Generar vista previa usando el microservicio de PDF.
         * Envía los mismos datos que se usan para la generación final.
         */
        async generatePreview() {
            if (!this.productPreviewLabelDesigner || !this.selectedLayout) return;

            const payload = this.buildPreviewPayload();
            await this.previewDesignToProducts(payload);
        },

        /**
         * Construir payload para la petición de preview.
         * Envía exactamente los mismos datos que la generación final (LabelsList.vue).
         * 
         * @returns {Object} Payload para la API
         */
        buildPreviewPayload() {
            const layout = this.selectedLayout;
            const filter = this.filter || {};
            
            const group = layout.company_id ? 'COMPANY_' + layout.company_id : 'COMPANY';
            
            const designWidth = parseFloat(layout.gridWidthCm) || 10;
            const designHeight = parseFloat(layout.gridHeightCm) || 10;
            
            // Construir objeto design completo
            const designData = {
                layout: layout.layout || [],
                countries_ids: layout.countries_ids || [],
                company_id: layout.company_id || null,
                orientation: true  
            };
            
            const design = {
                id: layout.id,
                title: layout.title,
                width: designWidth,
                height: designHeight,
                html_content: layout.html_content,
                data: JSON.stringify(designData),
                group: group
            };
            
            // Calcular dimensiones optimizadas para el papel del preview
            // Considera tanto el tamaño definido como el contenido real (secciones fuera del área)
            const sections = layout.layout || [];
            const { previewWidth, previewHeight } = this.calculatePreviewPaperDimensions(
                designWidth, 
                designHeight, 
                sections
            );
            
            const payload = {
                design: design,
                product_id: this.productPreviewLabelDesigner.id,
                company_id: filter.company || layout.company_id || null,
                country_id: filter.country_id || null,
                language_id: filter.language_id || null,
                design_id: layout.id || 0,
                paper_size: 'Otro',
                show_qr_code: filter.showQRcode ? 'show' : 'hide',
                quantity: 1,
                margin: 1,
                has_custom_paper_size: true,
                custom_paper_size: {
                    width: previewWidth,
                    height: previewHeight
                },
                orientation: true
            };

            return payload;
        },

        /**
         * Calcular dimensiones del papel para preview.
         * Analiza las secciones para detectar contenido fuera del área definida.
         * 
         * @param {number} designWidth - Ancho definido del diseño (cm)
         * @param {number} designHeight - Alto definido del diseño (cm)
         * @param {Array} sections - Array de secciones del layout
         * @returns {Object} { previewWidth, previewHeight }
         */
        calculatePreviewPaperDimensions(designWidth, designHeight, sections = []) {
            let actualContentHeight = designHeight;
            
            if (sections && sections.length > 0) {
                sections.forEach(section => {
                    const sectionY = parseFloat(section.y) || 0;
                    const sectionH = parseFloat(section.h) || 0;
                    const sectionBottomCm = (sectionY + sectionH) / 2;
                    
                    if (sectionBottomCm > actualContentHeight) {
                        actualContentHeight = sectionBottomCm;
                    }
                });
            }
            
            return {
                previewWidth: Math.ceil(designWidth),
                previewHeight: Math.ceil(actualContentHeight) + 1
            };
        },

        /**
         * Obtener orientación del diseño.
         * 
         * @returns {boolean} true = portrait, false = landscape
         */
        getOrientation() {
            if (!this.selectedLayout.layout) return true;
            
            const data = typeof this.selectedLayout.layout === 'string'
                ? JSON.parse(this.selectedLayout.layout)
                : this.selectedLayout.layout;
            
            return data.orientation !== false;
        },

        /**
         * Generar contenido HTML del diseño actual.
         * Este método se usa cuando el diseño aún no está guardado.
         * 
         * @returns {string|null} HTML del diseño
         */
        generateHtmlContent() {
            return this.selectedLayout.html_content || null;
        },

        /**
         * Reintentar generación de preview después de un error.
         */
        retryGeneratePreview() {
            if (this.productPreviewLabelDesigner) {
                this.generatePreview();
            }
        }

    },

    beforeDestroy() {
        // Limpiar recursos al destruir el componente
        this.clearPreview();
    }
}
</script>

<style scoped>
.content-process-label-design-sidebar {
    position: absolute;
    right: 0;
    width: 102%;    
    height: calc(100% + 48pt);
    overflow: auto;
    background: #f4f4f4ba;
    top: -21px;
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    overflow: hidden;
}

.content-process-label-design-sidebar-section-close {
    width: calc(100% - 479pt);
    height: calc(100% + 48pt);
}

.process-label-design-sidebar {
    height: 100vh;
    position: fixed;
    z-index: 1;
    top: 0;
    right: 0;
    background-color: #f2f2f2;
    overflow-x: hidden;
    padding-top: 60px;
    -webkit-transition: 0.5s;
    transition: 0.5s;
    overflow-y: auto;
    box-shadow: -4px 0 16px -3px #00000052;
}

.preview-pdf-container {
    min-height: 400px;
    max-height: 65vh;
    overflow-x: hidden;
    overflow-y: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
}

.empty-state {
    padding: 60px 20px;
    background: #fafafa;
    border-radius: 8px;
    border: 2px dashed #e0e0e0;
}

.empty-state-icon {
    font-size: 48px;
    color: #ccc;
}

@media (min-width: 0pt) and (max-width: 700pt) {
    .process-label-design-sidebar {
        height: 100vh;
        width: 100%;
        left: 0;
    }
}
</style>